import gsap from 'gsap';
import { translate } from './translate';
import { KingImages } from './constants/tutorialKingImages';
import { Store } from './store/gameStore';
import { Layout } from './LayoutManager';
import { MonoBehavior } from './core/monoBehavior';

export const KING_IMAGE_PATH = '/assets/tutorialKingPopup/';

export type PopupButton = {
    label: string;
    onClick: () => void;
};

export type TutorialPopupOptions = {
    contentKey: string;
    title?: string;
    kingIcon: KingImages;
    confirmButton?: PopupButton;
    skipButton?: PopupButton;
};

export class TutorialKingPopup extends MonoBehavior {
    options: TutorialPopupOptions;
    stepKey: string;
    popup: HTMLElement;
    kingIcon: KingImages;
    cloud?: HTMLDivElement;
    textDiv?: HTMLDivElement;
    isSmall = false;
    constructor(options: TutorialPopupOptions) {
        super();
        this.options = options;
        this.stepKey = options.contentKey;
        this.kingIcon = options.kingIcon;
        this.popup = document.createElement('div');

        this.create();
    }
    resize(): void {
        //60% width > height
        if (!this.popup || !this.textDiv || !this.cloud) return;
        const { width, height } = Layout.getTableSize();
        if (width / height > 2) {
            this.popup.classList.add('small');
            this.isSmall = true;
        } else {
            this.popup.classList.remove('small');
            this.isSmall = false;
        }

        this.autoResizeCloud(this.cloud, this.textDiv);
    }
    remove() {
        gsap.to(this.popup, {
            opacity: 0,
            onComplete: () => {
                //remove element from DOM
                this.popup.remove();
            },
        });
    }

    create() {
        if (!this.options.contentKey) return;

        this.popup.className = 'popup-with-king';
        this.popup.style.opacity = '0';

        const kingWrapper = document.createElement('div');
        kingWrapper.className = 'popup-king-wrapper';

        this.cloud = document.createElement('div');
        this.cloud.className = this.options.confirmButton || this.options.skipButton ? `popup-cloud popup-cloud-with-buttons` : `popup-cloud popup-cloud-no-buttons-${Store.name.toLowerCase()}`;

        const tail = document.createElement('img');
        tail.className = 'popup-tail';
        tail.src = '/assets/tutorialKingPopup/tail.svg';

        const kingIcon = document.createElement('img');
        kingIcon.className = 'popup-king-icon';
        kingIcon.src = this.options.kingIcon
            ? KING_IMAGE_PATH + this.options.kingIcon
            : KING_IMAGE_PATH + KingImages.LEFT_HAND;

        kingIcon.src =
            (this.options.kingIcon ? KING_IMAGE_PATH + this.options.kingIcon : KING_IMAGE_PATH + KingImages.LEFT_HAND) +
            (Store.webPSupported ? '.webp' : '.png');
        kingIcon.alt = 'King';

        if (this.options.title) {
            const titleDiv = document.createElement('div');
            titleDiv.className = 'popup-title';
            titleDiv.textContent = translate(this.options.title);
            this.cloud.appendChild(titleDiv);
        }

        this.textDiv = document.createElement('div');
        this.textDiv.className = 'popup-text';
        const contentHtml = translate(this.options.contentKey);
        this.textDiv.innerHTML = contentHtml;
        this.cloud.appendChild(this.textDiv);
        if (this.options.confirmButton) {
            const container = document.createElement('div');
            container.classList.add(
                'd-flex',
                'mt-2',
                'justify-content-end',
                'gap-3',
                'flex-row'
            );
            this.cloud.appendChild(container);

            if (this.options.skipButton) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.classList.add('modal_button', 'close', 'tutorial-skip-buttom');
                btn.textContent = translate(this.options.skipButton.label);
                btn.onclick = this.options.skipButton.onClick;
                container.appendChild(btn);
            }
            const btn = document.createElement('button');
            btn.classList.add('modals_area-btn');
            btn.textContent = translate(this.options.confirmButton.label);
            btn.onclick = this.options.confirmButton.onClick;
            container.appendChild(btn);
        }
        const bubbleContainer = document.createElement('div');
        bubbleContainer.className = this.options.confirmButton || this.options.skipButton ? 'popup-bubble-container popup-bubble-container-with-buttons' : 'popup-bubble-container popup-bubble-container-no-buttons';
        bubbleContainer.appendChild(tail);
        bubbleContainer.appendChild(this.cloud);

        kingWrapper.appendChild(kingIcon);
        kingWrapper.appendChild(bubbleContainer);

        this.popup.appendChild(kingWrapper);

        document.getElementById('solitaire-container')?.appendChild(this.popup);

        this.show(this.popup);
        this.autoResizeCloud(this.cloud, this.textDiv);
    }

    show(popup: HTMLElement) {
        gsap.to(popup, {
            opacity: 1,
        });
    }
    get hasButtons() {
        return !!(this.options.confirmButton || this.options.skipButton);
    }
    autoResizeCloud(cloud: HTMLDivElement, textDiv: HTMLDivElement) {
        cloud.style.removeProperty('width');
        cloud.style.removeProperty('max-width');
        const lineHeight = parseFloat(getComputedStyle(textDiv).lineHeight || '20'); // fallback line height
        const maxLines = this.isSmall || textDiv.textContent?.length < 80 ? 2 : 3;
        const maxHeight = lineHeight * maxLines;

        const currentHeight = textDiv.offsetHeight;

        if (currentHeight > maxHeight) {
            // Stretch width gradually until height is acceptable
            let width = textDiv.offsetWidth;
            const maxWidth = 600; // your design limit
            const step = 10;

            const adjust = () => {
                width += step;
                cloud.style.width = `${width}px`;
                const newHeight = textDiv.offsetHeight;

                if (newHeight > maxHeight && width < maxWidth) {
                    adjust();
                } else {
                    const margin = 16;
                    const rect = cloud.getBoundingClientRect();

                    const availableWidth = window.innerWidth - rect.left - margin;

                    cloud.style.maxWidth = `${availableWidth}px`;
                }
            };

            adjust();
        }
    }
}
